import { executionArtifactLineId } from '../families/execution-artifact.js';
import { memorySegmentId } from '../families/memory-layout.js';
import type { MemoryLayoutSpec } from '../model.js';
import type { ComparisonSpec } from '../primitives/comparison.js';
import type { RangeNumberLineSpec } from '../primitives/range-number-line.js';
import { artifactTerminalHeight, argumentHeight, fittedHeight, sequenceHeight } from './heights.js';
import type { GoLesson } from './types.js';

const TRUTH_FILE = 'go-slice-aliasing.json';
const GO = 'go1.24.4 windows/amd64';

/**
 * Slices share a backing array.
 *
 * Every number below comes out of kiln/content/truth/go-slice-aliasing.json:
 * the four len/cap values and the leaked 99 from `aliasing_len_cap`, the two
 * real backing-array addresses from `append_realloc`, the untouched original
 * from `fixed_copy`, and the corrupted HTTP body from `handler_reuse`.
 */
const mechanismFigure = (width: number, height: number): RangeNumberLineSpec => ({
        id: 'go-slice-aliasing.mechanism',
        family: 'range-number-line',
        view: 'orthographic',
        viewport: { width, height },
        subject: 'the backing array',
        quantity: 'slot',
        domain: { min: 0, max: 5 },
        tickStep: 1,
        bands: [
          { id: 'dropped', label: 'cut', from: 0, to: 1 },
          { id: 'kept', label: 'reachable by subslice', from: 1, to: 5 },
        ],
        threshold: { value: 1, label: 'the subslice starts at offset' },
        marks: [
          { id: 'original', label: 'original', from: 0, to: 5, detail: 'len 5 · cap 5' },
          { id: 'subslice', label: 'subslice', from: 1, to: 4, detail: 'len 3 · cap 4' },
          { id: 'write', label: 'the one write', from: 2, to: 3, detail: 'subslice[1] = 99 · original[2] reads 99' },
        ],
        companion: {
          label: 'the same slot, in subslice index',
          entries: [
            { at: 1.5, text: '[0]' },
            { at: 2.5, text: '[1]' },
            { at: 3.5, text: '[2]' },
          ],
        },
        focusMarkId: 'write',
        title: 'One array, two ways of counting into it',
        subtitle: 'Slot 2 of the array is slot 1 of the subslice, so one write lands in both readings.',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · aliasing_len_cap · extents from the printed len and cap values`,
      });

const choiceFigure = (width: number, height: number): ComparisonSpec => ({
        id: 'go-slice-aliasing.choice',
        family: 'comparison',
        view: 'table',
        viewport: { width, height },
        subject: 'a smaller slice',
        question: 'Reslice the array, or allocate and copy?',
        options: [
          { id: 'reslice', label: 'reslice', detail: 'original[1:4]' },
          { id: 'copy', label: 'copy', detail: 'make + copy' },
        ],
        criteria: [
          { id: 'arrays', label: 'Backing arrays in play', unit: 'arrays', first: 1, second: 2, better: 'lower' },
          { id: 'unchanged', label: 'Original elements unchanged', unit: 'of 5', first: 4, second: 5, better: 'higher', decisive: true },
        ],
        verdictOptionId: 'copy',
        verdictReason: 'Sharing is free until somebody writes, and then the bill arrives at code that never asked to share.',
        title: 'Reslicing wins on cost and still loses the decision',
        subtitle: 'Both rows are counted from captured runs: the leaked 99, and the original that came back whole.',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · aliasing_len_cap (one of five elements changed, one array) and fixed_copy (five of five unchanged, two arrays)`,
      });

const costFigure = (width: number, height: number): MemoryLayoutSpec => ({
        id: 'go-slice-aliasing.cost',
        family: 'memory-layout',
        view: 'orthographic',
        viewport: { width, height },
        baseAddress: '0xc000075d90',
        wordBytes: 8,
        typeName: '[4]int',
        segments: [
          { id: 'slot-0', label: 's[0]', bytes: 8, kind: 'field', value: 0 },
          { id: 'slot-1', label: 's[1]', bytes: 8, kind: 'field', value: 1 },
          { id: 'slot-2', label: 's[2]', bytes: 8, kind: 'field', value: 2 },
          { id: 'slot-3', label: 's[3]', bytes: 8, kind: 'field', value: 3 },
        ],
        focusSegmentId: memorySegmentId('slot-3'),
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · append_realloc · address printed by %x as c000075d90; int is 8 bytes on amd64, so cap 4 is 32 bytes`,
      });

export const goSliceAliasing: GoLesson = {
  id: 'go-slice-aliasing',
  module: '06 · Go foundations',
  title: 'Slices share a backing array',
  premise: 'A slice is a three-word header describing storage it does not own. Reslicing copies the header.',
  toolchain: GO,
  scenes: [
    {
      id: 'symptom',
      beat: 'symptom',
      stageLabel: 'SYMPTOM · CAPTURED RUN',
      heading: 'Nothing ever assigned to original[2].',
      subtitle: 'The only write in the program went to subslice[1]. Read original[2] anyway.',
      figureReason:
        'The claim is "this really happened", so the figure is the captured process record itself — the execution-artifact terminal, which is the only surface allowed to present bytes it did not compute.',
      code: {
        caption: 'main.go · demonstrate()',
        text: [
          'func demonstrate() {',
          '\toriginal := []int{1, 2, 3, 4, 5}',
          '\tsubslice := original[1:4]',
          '',
          '\tfmt.Printf("len of original: %d\\n", len(original))',
          '\tfmt.Printf("cap of original: %d\\n", cap(original))',
          '\tfmt.Printf("len of subslice: %d\\n", len(subslice))',
          '\tfmt.Printf("cap of subslice: %d\\n", cap(subslice))',
          '',
          '\tsubslice[1] = 99',
          '',
          '\tfmt.Printf("mutated element: %d\\n", original[2])',
          '}',
        ].join('\n'),
      },
      evidence: {
        caption: 'go run .',
        text: [
          'len of original: 5',
          'cap of original: 5',
          'len of subslice: 3',
          'cap of subslice: 4',
          'mutated element: 99',
          'mutation leaked through shared backing array',
        ].join('\n'),
      },
      determinism: '3 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'aliasing_len_cap' },
      quoted: [
        'len of original: 5',
        'cap of original: 5',
        'len of subslice: 3',
        'cap of subslice: 4',
        'mutated element: 99',
        'mutation leaked through shared backing array',
      ],
      spec: (width) => ({
        id: 'go-slice-aliasing.symptom',
        family: 'execution-artifact',
        view: 'artifact',
        artifactKind: 'terminal',
        surface: 'terminal',
        viewport: { width, height: artifactTerminalHeight(width, 7) },
        context: { tabLabel: 'go run', exitCode: 0 },
        title: 'One write, two slices',
        lines: [
          { id: 'command', kind: 'command', text: 'go run .' },
          { id: 'len-original', kind: 'output', text: 'len of original: 5' },
          { id: 'cap-original', kind: 'output', text: 'cap of original: 5' },
          { id: 'len-subslice', kind: 'output', text: 'len of subslice: 3' },
          { id: 'cap-subslice', kind: 'output', text: 'cap of subslice: 4' },
          { id: 'mutated', kind: 'evidence', text: 'mutated element: 99' },
          { id: 'verdict', kind: 'muted', text: 'mutation leaked through shared backing array' },
        ],
        focusLineId: executionArtifactLineId('mutated'),
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · aliasing_len_cap · stdout retained verbatim, 3 identical runs`,
      }),
    },
    {
      id: 'mechanism',
      beat: 'mechanism',
      stageLabel: 'MECHANISM · ONE STORAGE',
      heading: 'subslice[1] and original[2] are one slot.',
      subtitle: 'Reslicing moves where the window starts. It never moves the thing the window is over.',
      figureReason:
        'The assertion is positional: two index spaces name the same storage with an offset of one. A number line is the only figure where position is the claim, and its companion scale carries the second naming without redrawing the array.',
      code: {
        caption: 'main.go · the two headers',
        text: ['\toriginal := []int{1, 2, 3, 4, 5}', '\tsubslice := original[1:4]', '\tsubslice[1] = 99'].join('\n'),
      },
      evidence: {
        caption: 'go run .',
        text: ['len of original: 5', 'len of subslice: 3', 'cap of subslice: 4', 'mutated element: 99'].join('\n'),
      },
      determinism: '3 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'aliasing_len_cap' },
      quoted: ['len of original: 5', 'len of subslice: 3', 'cap of subslice: 4', 'mutated element: 99'],
      spec: (width) => mechanismFigure(width, fittedHeight((height) => mechanismFigure(width, height), 470)),
    },
    {
      id: 'choice',
      beat: 'choice',
      stageLabel: 'CHOICE · TWO REAL EDITS',
      heading: 'Reslice, or make and copy?',
      subtitle: 'Both hand you a slice of the same values. Only one hands you an array of your own.',
      figureReason:
        'A decision between two real edits, each measured. The comparison primitive derives the winner of every row from a declared direction, and lets one criterion outrank the tally — which is exactly the shape of this trade.',
      code: {
        caption: 'main.go · the copy path',
        text: ['\tcopied := make([]int, len(original))', '\tcopy(copied, original)', '\t', '\tcopied[1] = 999', '\tcopied[3] = 888'].join('\n'),
      },
      evidence: {
        caption: 'go run . · fixed_copy',
        text: ['original before: [10 20 30 40 50]', 'original after: [10 20 30 40 50]'].join('\n'),
      },
      determinism: '3 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'fixed_copy' },
      quoted: ['original before: [10 20 30 40 50]', 'original after: [10 20 30 40 50]'],
      spec: (width) => choiceFigure(width, fittedHeight((height) => choiceFigure(width, height), 500)),
    },
    {
      id: 'trace',
      beat: 'trace',
      stageLabel: 'TRACE · WATCH THE ADDRESS',
      heading: 'The address holds, holds, holds — then jumps.',
      subtitle: 'Three appends fit inside cap 4. The fourth has to go and find somewhere else.',
      figureReason:
        'The evidence is a printed address series; the whole argument is that eight lines of it show one value changing exactly once. Any redrawing would be weaker than the capture, so the capture is the figure.',
      code: {
        caption: 'main.go · append loop',
        text: [
          '\tfor i := 1; i < 8; i++ {',
          '\t\taddrBefore := uintptr(unsafe.Pointer(&s[0]))',
          '',
          '\t\ts = append(s, i)',
          '',
          '\t\taddrAfter := uintptr(unsafe.Pointer(&s[0]))',
        ].join('\n'),
      },
      evidence: {
        caption: 'go run . · append_realloc',
        text: [
          'len=1 cap=4 addr=c000075d90',
          'len=2 cap=4 addr=c000075d90',
          'len=3 cap=4 addr=c000075d90',
          'len=4 cap=4 addr=c000075d90',
          'len=5 cap=8 addr=c000016500',
          'len=6 cap=8 addr=c000016500',
          'len=7 cap=8 addr=c000016500',
          'len=8 cap=8 addr=c000016500',
        ].join('\n'),
      },
      determinism: '3 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'append_realloc' },
      quoted: [
        'len=1 cap=4 addr=c000075d90',
        'len=2 cap=4 addr=c000075d90',
        'len=3 cap=4 addr=c000075d90',
        'len=4 cap=4 addr=c000075d90',
        'len=5 cap=8 addr=c000016500',
        'len=6 cap=8 addr=c000016500',
        'len=7 cap=8 addr=c000016500',
        'len=8 cap=8 addr=c000016500',
      ],
      spec: (width) => ({
        id: 'go-slice-aliasing.trace',
        family: 'execution-artifact',
        view: 'artifact',
        artifactKind: 'terminal',
        surface: 'terminal',
        viewport: { width, height: artifactTerminalHeight(width, 9) },
        context: { tabLabel: 'go run', exitCode: 0 },
        title: 'One address per state · the EVENT lines between are omitted',
        lines: [
          { id: 'command', kind: 'command', text: 'go run .' },
          { id: 'state-1', kind: 'output', text: 'len=1 cap=4 addr=c000075d90' },
          { id: 'state-2', kind: 'output', text: 'len=2 cap=4 addr=c000075d90' },
          { id: 'state-3', kind: 'output', text: 'len=3 cap=4 addr=c000075d90' },
          { id: 'state-4', kind: 'output', text: 'len=4 cap=4 addr=c000075d90' },
          { id: 'state-5', kind: 'evidence', text: 'len=5 cap=8 addr=c000016500' },
          { id: 'state-6', kind: 'output', text: 'len=6 cap=8 addr=c000016500' },
          { id: 'state-7', kind: 'output', text: 'len=7 cap=8 addr=c000016500' },
          { id: 'state-8', kind: 'output', text: 'len=8 cap=8 addr=c000016500' },
        ],
        focusLineId: executionArtifactLineId('state-5'),
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · append_realloc · 8 of the 34 printed lines, each verbatim; the EVENT lines between them are omitted`,
      }),
    },
    {
      id: 'cost',
      beat: 'trade-off',
      stageLabel: 'COST · WHY IT HAD TO MOVE',
      heading: 'cap 4 is 32 bytes, and all four are taken.',
      subtitle: 'At len=4 cap=4 the array at 0xc000075d90 is full, so the fifth value needs a new one.',
      figureReason:
        'The question is extent: how much storage the header is describing, and where it stops. Memory layout is the orthographic view whose whole job is exact byte offsets against a real base address.',
      code: {
        caption: 'main.go · the allocation',
        text: ['\ts := make([]int, 1, 4)', '\ts[0] = 0'].join('\n'),
      },
      evidence: {
        caption: 'go run . · append_realloc',
        text: ['len=4 cap=4 addr=c000075d90', 'len=5 cap=8 addr=c000016500'].join('\n'),
      },
      determinism: '3 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'append_realloc' },
      quoted: ['len=4 cap=4 addr=c000075d90', 'len=5 cap=8 addr=c000016500'],
      spec: (width) => costFigure(width, fittedHeight((height) => costFigure(width, height), 470)),
    },
    {
      id: 'production',
      beat: 'production',
      stageLabel: 'PRODUCTION · POOLED BUFFER',
      heading: "Request 2 answered with request 1's bytes.",
      subtitle: 'One reused buffer, two subslices of it, and a loop that lower-cased in place.',
      figureReason:
        'This is an ordering failure across a shared object: what each request did to the buffer, and when. A sequence keeps one lane per participant, so the shared lane is visibly the one both of them wrote to.',
      code: {
        caption: 'main.go · handler_reuse',
        text: [
          '\tbuf := make([]byte, 100)',
          '',
          '\treq1Data := []byte("REQUEST-1-DATA")',
          '\tcopy(buf, req1Data)',
          '\treq1Body := buf[:len(req1Data)]',
          '',
          '\treq2Data := []byte("req2")',
          '\tcopy(buf, req2Data)',
          '\treq2Body := buf[:len(req1Data)]',
        ].join('\n'),
      },
      evidence: {
        caption: 'go run . · handler_reuse',
        text: ['request 1 body: req2est-1-data', 'request 2 body: req2est-1-data', 'corrupted body: req2est-1-data'].join('\n'),
      },
      determinism: '3 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'handler_reuse' },
      quoted: ['request 1 body: req2est-1-data', 'request 2 body: req2est-1-data', 'corrupted body: req2est-1-data'],
      spec: (width) => ({
        id: 'go-slice-aliasing.production',
        family: 'concurrency-sequence',
        view: 'sequence',
        viewport: { width, height: sequenceHeight(width, 6) },
        participants: [
          { id: 'req1', label: 'req 1', role: 'actor' },
          { id: 'body1', label: 'body 1', role: 'storage' },
          { id: 'buf', label: 'buf', role: 'storage' },
          { id: 'req2', label: 'req 2', role: 'actor' },
        ],
        events: [
          { id: 'fill-1', from: 'req1', to: 'buf', label: 'copy 14B', kind: 'call' },
          { id: 'alias-1', from: 'buf', to: 'body1', label: 'buf[:14]', kind: 'return' },
          { id: 'lower', from: 'body1', to: 'buf', label: 'lower-case', kind: 'call' },
          { id: 'fill-2', from: 'req2', to: 'buf', label: 'copy 4B', kind: 'call' },
          { id: 'alias-2', from: 'buf', to: 'req2', label: 'buf[:14]', kind: 'return' },
          { id: 'corrupt', from: 'buf', to: 'body1', label: 'overwritten', kind: 'error' },
        ],
        focusEventId: 'corrupt',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · handler_reuse · both printed bodies read req2est-1-data; buf[:14] is buf[:len(req1Data)] and 14B/4B are the lengths of the two payloads in the captured source`,
      }),
    },
    {
      id: 'model',
      beat: 'model',
      stageLabel: 'MODEL · WHY IT FOLLOWS',
      heading: 'A slice is a view, not a container.',
      subtitle: 'Two headers can name the same bytes, so a write through either is visible through both.',
      figureReason:
        'The closing scene owes the learner a derivation, not a summary. The annotated argument gives every line an expression, a rationale and a stated dependence on the line above it.',
      code: {
        caption: 'main.go · the whole mechanism',
        text: ['\tsubslice := original[1:4]', '\tsubslice[1] = 99'].join('\n'),
      },
      evidence: {
        caption: 'go run .',
        text: ['mutated element: 99', 'mutation leaked through shared backing array'].join('\n'),
      },
      determinism: '3 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'aliasing_len_cap' },
      quoted: ['mutated element: 99', 'mutation leaked through shared backing array'],
      spec: (width) => ({
        id: 'go-slice-aliasing.model',
        family: 'complexity-argument',
        view: 'annotated-argument',
        viewport: { width, height: argumentHeight(width, 4) },
        claim: 'A write reaches every view of the array',
        steps: [
          {
            id: 'header',
            kind: 'given',
            expression: 's = { ptr, len, cap }',
            rationale: 'Three machine words: where the storage starts, how much is addressable, how much reserved.',
          },
          {
            id: 'reslice',
            kind: 'transform',
            expression: 'original[1:4] copies the header',
            rationale: 'The capture prints len 3 and cap 4 over an array of 5: a new header, the same array.',
          },
          {
            id: 'index',
            kind: 'transform',
            expression: 's[i] resolves to ptr + i',
            rationale: 'Indexing is arithmetic on the pointer, so two headers can resolve to one slot.',
          },
          {
            id: 'conclusion',
            kind: 'conclusion',
            expression: 'one write, every view',
            rationale: 'subslice[1] = 99 wrote the slot original calls 2, and the run prints 99 there.',
          },
        ],
        focusStepId: 'conclusion',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · aliasing_len_cap · every quantity in the derivation is a printed value`,
      }),
    },
  ],
};
